import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Box,
  Divider,
  Avatar,
  TextField,
  Grid,
  Chip,
  Alert,
  ButtonGroup,
  CircularProgress
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Medication as MedicationIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { loadPatientsMedHistoryAdmission } from '../BaseURL';
import moment from "moment";

const MedicalHistoryDialogAdmission = ({ open, onClose, id ,sendstartDate }) => {
const enddate = moment().format('YYYY-MM-DD')
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('All');
  const [medicationHistory, setMedicationHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Period filter options
  const periodOptions = [
    { label: 'Today', value: 'Today' },
    { label: 'This Week', value: 'This Week' },
    { label: 'This Month', value: 'This Month' },
    { label: 'This Year', value: 'This Year' },
    { label: 'All', value: 'All' }
  ];

  // Function to get date range based on selected period
  // Function to get date range based on selected period
const getDateRangeForPeriod = (period) => {
  const today = new Date();
  // FIX: Create dates in local timezone to avoid day-behind issue
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  switch (period) {
    case 'Today':
      return { from: formatDate(today), to: formatDate(today) };
    case 'This Week':
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return { from: formatDate(startOfWeek), to: formatDate(endOfWeek) };
    case 'This Month':
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { from: formatDate(startOfMonth), to: formatDate(endOfMonth) };
    case 'This Year':
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      const endOfYear = new Date(today.getFullYear(), 11, 31);
      return { from: formatDate(startOfYear), to: formatDate(endOfYear) };
    case 'All':
    default:
      return { from: '', to: '' };
  }
};

  // Handle period selection
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    const dateRange = getDateRangeForPeriod(period);
    setFromDate(dateRange.from);
    setToDate(dateRange.to);
  };

  const loadMedHistory = async () => {
    // Don't load if there's no ID or dialog is not open
    if (!id || !open) {
     // console.log('Skipping load - no ID or dialog closed:', { id, open });
      return;
    }

    setLoading(true);
    setError(null);

    try {
     // console.log('Loading medication history for ID:', id);
     // console.log('API endpoint:', loadPatientsMedHistory);
      
      const response = await axios.post(loadPatientsMedHistoryAdmission, { id ,sendstartDate ,enddate });
      
      console.log('API Response:', response.data);
      
      if (response.data.success) {
        setMedicationHistory(response.data.results || []);
       // console.log('Medication history loaded:', response.data.results);
      } else {
        const errorMsg = response.data.message || 'Failed to load medication history';
        setError(errorMsg);
       // console.error('API returned unsuccessful response:', response.data);
      }
      
    } catch (error) {
      console.error('Error loading medication history:', error);
      
      const errorMessage = error.response?.data?.message || 
             error.message || 
        'Failed to Load Data';
      
      setError(errorMessage);
      
      // Show error popup
      await Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Load data when component mounts or when id/open changes
  useEffect(() => {
    console.log('useEffect triggered - open:', open, 'id:', id);
    
    if (open && id) {
      loadMedHistory();
    } else {
      // Reset state when dialog closes or no ID
      setMedicationHistory([]);
      setError(null);
    }
  }, [open, id]); // Added 'open' to dependencies

  // Filter and group medications based on date range
// Filter and group medications based on date range
const filteredAndGroupedMedicationHistory = useMemo(() => {
  let filteredMedications = medicationHistory;

  // Apply date filtering if dates are set
  if (fromDate || toDate) {
    filteredMedications = medicationHistory.filter((med) => {
      // FIX: Compare date strings directly instead of creating Date objects
      const medDate = med.date; // Assuming med.date is already in YYYY-MM-DD format
      
      let includeRecord = true;
      
      if (fromDate && medDate < fromDate) {
        includeRecord = false;
      }
      
      if (toDate && medDate > toDate) {
        includeRecord = false;
      }
      
      return includeRecord;
    });
  }

  // Group filtered medications by date
  const grouped = {};
  filteredMedications.forEach((med) => {
    if (!grouped[med.date]) {
      grouped[med.date] = [];
    }
    grouped[med.date].push(med);
  });

  return grouped;
}, [fromDate, toDate, medicationHistory]);

 const formatDate = (dateStr) => {
  // FIX: Parse the date string correctly without timezone conversion
  const [year, month, day] = dateStr.split('-');
  const date = new Date(year, month - 1, day); // Creates date in local timezone
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};
  const clearFilters = () => {
    setFromDate('');
    setToDate('');
    setSelectedPeriod('All');
  };

  const hasActiveFilters = fromDate || toDate;
  const filteredEntryCount = Object.keys(filteredAndGroupedMedicationHistory).length;
  const totalEntryCount = [...new Set(medicationHistory.map(med => med.date))].length;

  return (
   
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '700px',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
              <MedicationIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" component="div">
                Patient ID: {id || 'Unknown'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Medication History
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ 
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading medication history...</Typography>
            </Box>
          )}

        
          {/* Main Content - only show when not loading */}
          {!loading && (
            <>
              {/* Date Filter Section */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <FilterIcon color="primary" />
                  <Typography variant="h6" color="primary.main">
                    Patient Medication History  
                  </Typography>
                  {hasActiveFilters && (
                    <Chip
                      label={`${filteredEntryCount} of ${totalEntryCount} records`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
                
                {/* Combined Period Filter and Date Inputs on One Line */}
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        Filter by Period:
                      </Typography>
                      <ButtonGroup size="small" variant="outlined">
                        {periodOptions.map((option) => (
                          <Button
                            key={option.value}
                            variant={selectedPeriod === option.value ? 'contained' : 'outlined'}
                            onClick={() => handlePeriodChange(option.value)}
                            sx={{
                              minWidth: '50px',
                              px: 1,
                              py: 1.3,
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              backgroundColor: selectedPeriod === option.value ? '#10b981' : 'transparent',
                              borderColor: selectedPeriod === option.value ? '#10b981' : '#d1d5db',
                              color: selectedPeriod === option.value ? 'white' : '#374151',
                              '&:hover': {
                                backgroundColor: selectedPeriod === option.value ? '#059669' : '#f9fafb',
                                borderColor: selectedPeriod === option.value ? '#059669' : '#9ca3af',
                              }
                            }}
                          >
                            {option.label}
                          </Button>
                        ))}
                      </ButtonGroup>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={2.5}>
                    <TextField
                      label="From Date"
                      type="date"
                      value={fromDate}
                      onChange={(e) => {
                        setFromDate(e.target.value);
                        setSelectedPeriod(''); // Clear period selection when manual date is set
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={2.5}>
                    <TextField
                      label="To Date"
                      type="date"
                      value={toDate}
                      onChange={(e) => {
                        setToDate(e.target.value);
                        setSelectedPeriod(''); // Clear period selection when manual date is set
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={2}>
                    {hasActiveFilters && (
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<ClearIcon />}
                        onClick={clearFilters}
                        fullWidth
                        size="small"
                      >
                        Clear Filters
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Box>

              {/* Results Info */}
              {hasActiveFilters && (
                <Box sx={{ mb: 2 }}>
                  {filteredEntryCount === 0 ? (
                    <Alert severity="info">
                      No medication records found for the selected date range.
                    </Alert>
                  ) : (
                    <Alert severity="success">
                      Showing {filteredEntryCount} medication record{filteredEntryCount !== 1 ? 's' : ''} 
                      {fromDate && ` from ${formatDate(fromDate)}`}
                      {toDate && ` to ${formatDate(toDate)}`}
                    </Alert>
                  )}
                </Box>
              )}

            

              {/* Medication History */}
              <Box sx={{ space: 4 }}>
                {Object.keys(filteredAndGroupedMedicationHistory).length === 0 && !hasActiveFilters ? (
                  <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                    {medicationHistory.length === 0 ? 'No medication history available.' : 'No records match the current filters.'}
                  </Typography>
                ) : (
                  Object.entries(filteredAndGroupedMedicationHistory)
                    .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
                    .map(([date, medications], index) => (
                    <Box key={date} sx={{ mb: index < Object.keys(filteredAndGroupedMedicationHistory).length - 1 ? 4 : 0 }}>
                      {/* Date Header */}
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: 'primary.main',
                          fontWeight: 600,
                          mb: 2
                        }}
                      >
                        {formatDate(date)}
                      </Typography>
                      <Divider sx={{ mb: 2, borderColor: 'primary.light' }} />

                      {/* Medications Table */}
                      <TableContainer 
                        component={Paper} 
                        variant="outlined"
                        sx={{
                          maxHeight: medications.length > 5 ? '320px' : 'auto',
                          overflowY: medications.length > 5 ? 'auto' : 'visible',
                          '& .MuiTable-root': {
                            '& .MuiTableHead-root': {
                              position: medications.length > 5 ? 'sticky' : 'static',
                              top: 0,
                              zIndex: 1,
                              '& .MuiTableRow-root': {
                                bgcolor: 'grey.50'
                              }
                            }
                          }
                        }}
                      >
                        <Table stickyHeader={medications.length > 5}>
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                              <TableCell sx={{ fontWeight: 600 }}>
                                Medication Name
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                Route
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                Frequency
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                Duration
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }} align="center">
                                Quantity
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }} align="center">
                                Prescribed By
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }} align="center">
                                Served By
                              </TableCell>

                               <TableCell sx={{ fontWeight: 600 }} align="center">
                                Status
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {medications.map((med, medIndex) => (
                              <TableRow 
                                key={medIndex}
                                hover
                                sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                              >
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {med.name}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {med.route}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {med.frequency}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {med.days}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {med.quantity}
                                  </Typography>
                                </TableCell>

                                 <TableCell>
                                  <Typography variant="body2">
                                    {med.doc_name}
                                  </Typography>
                                </TableCell>

                                 <TableCell>
                                  <Typography variant="body2">
                                    {med.user}
                                  </Typography>
                                </TableCell>

                                 <TableCell>
                                  <Typography variant="body2">
                                    {med.status}
                                  </Typography>
                                </TableCell>

                                
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ))
                )}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ bgcolor: 'grey.50', p: 2 }}>
          <Button 
            onClick={onClose} 
            variant="contained" 
            color="primary"
            size="large"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    
  );
};

export default MedicalHistoryDialogAdmission;



